'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useAuth } from '@/features/auth/hooks'
import { capRfqAccepts, upsertRfqs } from '@/features/rfq/lib/rfq-store'
import { useActiveAccountsScope } from '@/service/account.hook'
import { applyPushedPositionsView } from '@/service/position-group.hook'
import { useRuntimeRecovery } from '@/service/runtime-health/use-runtime-recovery'
import { buildRfqStreamPayload } from '@/service/ws/rfq-stream-scope'
import { useRfqAcceptStream, useRfqStream } from '@/service/ws/use-rfq-stream'
import { useWebSocketContext } from '@/service/ws/websocket-context'
import { rfqAcceptsAtom, rfqsRawAtom } from '@/state/rfq'
import type { PortfolioData } from '@/types/generated/common/account'
import { PositionViewMode, type ServerMessage } from '@/types/generated/public_api'
import { RfqAccept, RfqUpdateList } from '@/types/generated/rfq'

export interface HistoryFilterParams {
  symbols?: string[]
  currency?: string[]
  accounts?: string[]
  from?: string
  to?: string
}

/**
 * AccountDataManager component
 * Handles RFQs across every active account and the AUTO/CUSTOM position-view push.
 * Balances/Summary/Fees moved to <ActiveAccountSnapshotSync />;
 * Positions moved to <ActivePositionsSync />; Orders/Order History/Trades/Algo
 * states moved to <ActiveOrdersSync />/<ActiveTradesSync /> — all fan out one
 * subscription per active account instead of tracking the single selected one.
 *
 * Place this component globally (e.g., in layout or a root provider).
 */
export function AccountDataManager() {
  const { accountIds, hasAccounts } = useActiveAccountsScope()
  const { isAuthenticated } = useAuth()

  const queryClient = useQueryClient()
  const setRfqs = useSetAtom(rfqsRawAtom)
  const setRfqAccepts = useSetAtom(rfqAcceptsAtom)

  const { addChannelListener, removeChannelListener, restart } = useWebSocketContext()

  useRuntimeRecovery()

  // Track previous auth state to detect demo → real login transition
  const prevIsAuthenticatedRef = useRef(isAuthenticated)

  // ============================================
  // RFQS
  // ============================================
  // One batched subscription covers every active account. Gate on hasAccounts so the
  // unresolved/zero-account state never sends accountIds: [], which the backend treats
  // as an unbounded "every readable account" scope. No snapshot arrives on subscribe;
  // the displayed list is seeded from get_rfq_history.
  const rfqPayload = useMemo(() => buildRfqStreamPayload(accountIds), [accountIds])

  const handleRfqsMessage = useCallback(
    (data: RfqUpdateList | undefined) => {
      if (data?.updates?.length) {
        setRfqs((prev) => upsertRfqs(prev, data.updates))
      }
    },
    [setRfqs]
  )

  useRfqStream({
    connected: hasAccounts,
    payload: rfqPayload,
    onMessage: handleRfqsMessage,
  })

  const handleRfqAccept = useCallback(
    (accept: RfqAccept | undefined) => {
      if (accept?.rfqId) {
        setRfqAccepts((prev) => capRfqAccepts({ ...prev, [accept.rfqId]: accept }))
      }
    },
    [setRfqAccepts]
  )

  useRfqAcceptStream({
    connected: hasAccounts,
    payload: rfqPayload,
    onMessage: handleRfqAccept,
  })

  // ─── AUTO/CUSTOM view push listener ──────────────────────
  // The server pushes GetPositionsViewResponse on the accountStream channel after every
  // position delta, piggybacking on the existing positions subscription. A push is the
  // unfiltered (all-accounts) view; it lands in the same React Query cache the HTTP fetch
  // uses, keyed by mode + account filter, so widgets with different filters never share
  // one view.
  useEffect(() => {
    if (!hasAccounts) return

    const handleView = (message: ServerMessage) => {
      const viewResponse = message.getPositionsViewResponse
      if (
        viewResponse?.mode === PositionViewMode.POSITION_VIEW_MODE_AUTO ||
        viewResponse?.mode === PositionViewMode.POSITION_VIEW_MODE_CUSTOM
      ) {
        applyPushedPositionsView(queryClient, viewResponse)
      }
    }

    addChannelListener('accountStream', handleView)
    return () => removeChannelListener('accountStream', handleView)
  }, [hasAccounts, addChannelListener, removeChannelListener, queryClient])

  // NOTE: triggerResyncAtom deliberately does NOT reconnect here any more.
  // The recovery controller (useRuntimeRecovery above) owns socket restarts via
  // the provider's restart(); the atom now means "reset your dump trackers" and
  // nothing else. Reinstating a disconnect/connect on it would restart the
  // socket twice for every recovery — once here, once in the controller.
  // Balances'/Positions'/Orders'/Trades' dump flags live per-account in
  // <ActiveAccountSnapshotSync />/<ActivePositionsSync />/<ActiveOrdersSync />/
  // <ActiveTradesSync />, which read the atom and reset themselves in lockstep.

  // Reconnect WS on auth transition
  useEffect(() => {
    const wasAuthenticated = prevIsAuthenticatedRef.current
    prevIsAuthenticatedRef.current = isAuthenticated

    if (!wasAuthenticated && isAuthenticated) {
      // Not a recovery — a genuinely new token needs a fresh authenticated
      // socket. Goes through restart() so the close-then-reopen sequence stays
      // in one place. Balances'/Positions'/Orders'/Trades' own dump flags live
      // per-account in the sync components above, which watch isAuthenticated
      // themselves.
      void restart()
    }
  }, [isAuthenticated, restart])

  return null
}

/**
 * Merges portfolio data (balances).
 *
 * Exported for reuse by per-account portfolio streaming (see
 * service/ws/account-snapshot-sync.tsx), which mirrors the dump/merge logic
 * here but scopes to a single explicitly-passed accountId instead of the
 * globally-selected exchangeAtom account.
 */
export function mergePortfolioData(
  prev: PortfolioData[],
  updates: PortfolioData[]
): PortfolioData[] {
  const result = [...prev]

  updates.forEach((newData) => {
    // Match by currency and accountId
    const index = result.findIndex(
      (p) =>
        p.currency === newData.currency &&
        (!p.accountId || !newData.accountId || p.accountId === newData.accountId)
    )

    if (index !== -1) {
      // Merge skipping empty fields - allow 0 as it's valid for balances
      result[index] = {
        ...result[index],
        ...Object.fromEntries(
          Object.entries(newData).filter(([_, v]) => v !== '' && v !== undefined)
        ),
      }
    } else {
      result.push(newData)
    }
  })

  return result
}
